import React, { memo, useCallback } from 'react';

import { Box, Button, Typography } from '@mui/material';
import LabelCDS from '@sharedCR/coreDesignSystem/LabelCDS';
import { LabelSize, LabelVariant } from '@sharedCR/coreDesignSystem/LabelCDS/LabelCDS.constants';
import PropTypes from 'prop-types';

import { getCicDisplayValue } from 'utils/cicReason';

import { EDITED_CHIP_TITLE, ReasonEditKind } from '../../LoanCicHistory.constants';
import { cicChangePropTypes } from '../../LoanCicHistory.propTypes';
import { struckOutTextStyles } from '../../LoanCicHistory.styles';
import { LoanCicHistoryTestIds } from '../../LoanCicHistory.testIds';
import { getReasonEditSummary } from '../../LoanCicHistory.utils';

import { ORIGINAL_REASON_PREFIX } from './CicReasonCell.constants';
import { historyLinkStyles, reasonCellStyles, reasonLineStyles } from './CicReasonCell.styles';
import {
  getEditedByLabel,
  getEditHistoryLinkLabel,
  getOriginalReasonSuffix,
} from './CicReasonCell.utils';

const CicReasonCell = ({ change, isHistoryLinkVisible = false, onOpenHistory = null }) => {
  const { id, reasonName, revisions } = change;

  const editSummary = getReasonEditSummary(change);
  const isEdited = !!editSummary;
  const isOriginalReplaced = editSummary?.kind === ReasonEditKind.ORIGINAL_REPLACED;

  const onHistoryClick = useCallback(() => onOpenHistory?.(change), [onOpenHistory, change]);

  return (
    <Box sx={reasonCellStyles}>
      <Box sx={reasonLineStyles}>
        <Typography data-testid={LoanCicHistoryTestIds.getChangeReasonId(id)} variant="regular14">
          {getCicDisplayValue(reasonName)}
        </Typography>
        {isEdited && (
          <Box data-testid={LoanCicHistoryTestIds.getChangeEditedLabelId(id)}>
            <LabelCDS size={LabelSize.S} title={EDITED_CHIP_TITLE} variant={LabelVariant.GRAY} />
          </Box>
        )}
      </Box>

      {isOriginalReplaced && (
        <Typography color="text.secondary" variant="regular12">
          {ORIGINAL_REASON_PREFIX}
          <Typography component="span" sx={struckOutTextStyles} variant="regular12">
            {editSummary.originalReasonName}
          </Typography>
          {getOriginalReasonSuffix(editSummary)}
        </Typography>
      )}

      {isEdited && !isOriginalReplaced && (
        <Typography color="text.secondary" variant="regular12">
          {getEditedByLabel(editSummary)}
        </Typography>
      )}

      {isEdited && isHistoryLinkVisible && (
        <Button
          data-testid={LoanCicHistoryTestIds.getChangeHistoryLinkId(id)}
          size="small"
          sx={historyLinkStyles}
          variant="text"
          onClick={onHistoryClick}
        >
          {getEditHistoryLinkLabel(revisions.length)}
        </Button>
      )}
    </Box>
  );
};

CicReasonCell.propTypes = {
  change: cicChangePropTypes.isRequired,
  isHistoryLinkVisible: PropTypes.bool,
  onOpenHistory: PropTypes.func,
};

export default memo(CicReasonCell);
